import {
  formatDate,
  formatDateColumnValue,
  formatDateTimeByLocale,
  formatNumberColumnValue,
  isLookupCellData,
  isValidArrayInnerType,
  OrganizationFeatureFlag,
} from '@doss/common-all'
import { Box } from '@mui/material'
import Linkify from 'linkify-react'
import { first, isNil, isUndefined } from 'lodash'
import { MdError } from 'react-icons/md'
import { Checkbox } from '@/components/ui/checkbox-v2'
import { DataType } from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { getFormulaErrorMessageKey } from '~/common/utils/formula-error'
import { getEnumValueColorAndIcon } from '~/components/shared/enum-options'
import { enumSourceForLookupColumn } from '~/components/shared/lookup-enum'
import { Text } from '~/components/ui/atoms/text/Text'
import { TEXT_LENGTH_LIMIT } from '../../common/utils/table'
import { useLookupContext } from '../records/lookup/hooks/LookupProvider'
import { ActiveUser } from '../settings/types'
import { EnumOptionMark } from '../shared/EnumOptionMark'
import { DetailedTableColumn, DossTableCell } from '../tables/types'
import { Tooltip } from '../ui/atoms/tooltip/Tooltip'
import { LookupCellChipV2 } from '../ui/molecules/lookup-cell-chip/LookupCellChipV2'
import { UserAvatar } from '../users/UserAvatar'
import { ArrayTableCell } from './ArrayTableCells/ArrayTableCell'
import { getTableCellStyles } from './TableCell'
import { TableCellLoadingValue } from './TableCellLoadingValue'

// Inter's contextual ligatures substitute "x" between digits with the multiplication sign "×".
// Disable them for ID cells so values like "1x23" render literally.
const idTextFeatures = {
  fontVariantLigatures: 'no-contextual',
} as const

interface FormattedTableCellComponentProps {
  value: string[] | string | boolean | number | null | undefined
  tableColumnHeader: DetailedTableColumn
  cell: DossTableCell
  styles: ReturnType<typeof getTableCellStyles>
  textWrap: ReturnType<typeof getTableCellStyles> | object
  fileContent: JSX.Element | null
  showAutogeneratedIdTooltip: boolean
  maxColWidth: number
  readOnly: boolean
  activeUsers: ActiveUser[]
}

/**
 * Returns the formatted cell value based on the column type
 */
export const FormattedTableCellComponent = ({
  value,
  tableColumnHeader,
  cell,
  styles,
  textWrap,
  fileContent,
  showAutogeneratedIdTooltip,
  maxColWidth,
  readOnly,
  activeUsers,
}: FormattedTableCellComponentProps) => {
  const { t } = useTypedTranslation()
  const { locale } = useGlobalContext()
  const { getCellDisplay } = useLookupContext()
  const { user } = useGlobalContext()

  const numericFormulaRuntimeErrorsEnabled =
    user?.organizationFeatureFlags[OrganizationFeatureFlag.NumericFormulaRuntimeErrors] === true
  const isNumericFormula = tableColumnHeader.type === DataType.FORMULA_NUMBER
  const formulaErrorMessageKey = getFormulaErrorMessageKey(
    cell.formulaError,
    isNumericFormula,
    numericFormulaRuntimeErrorsEnabled,
  )
  const formulaErrorMessage = formulaErrorMessageKey ? t(formulaErrorMessageKey) : undefined
  const formulaErrorIndicator = formulaErrorMessage ? (
    <Tooltip arrow title={formulaErrorMessage}>
      <span className="inline-flex shrink-0">
        <MdError className="text-red-8" size={16} />
      </span>
    </Tooltip>
  ) : null

  // If the cell is undefined and the column is NOT of type array, it means the data
  // has not been denormalized yet. For array type columns, undefined values might occur
  // due to Elasticsearch field-level security behavior, which can remove empty arrays
  // entirely from results when the user doesn't have admin permissions.
  if (isUndefined(value) && tableColumnHeader.type !== DataType.ARRAY) {
    // This cell's data has not been denormalized yet
    return <TableCellLoadingValue />
  }

  switch (tableColumnHeader.type) {
    case DataType.ARRAY:
      if (isNil(value) || value === '') {
        return null
      }

      if (Array.isArray(value) && isValidArrayInnerType(tableColumnHeader.innerType)) {
        return (
          <ArrayTableCell
            activeUsers={activeUsers}
            cell={cell}
            displayImage={tableColumnHeader.displayImagesInline}
            readOnly={readOnly}
            tableColumn={tableColumnHeader}
            value={value}
          />
        )
      }

      throw new Error('Invalid value or inner type for ARRAY column')

    case DataType.ENUM: {
      if (isNil(value) || value === '') {
        return null
      }
      const { color, iconName } = getEnumValueColorAndIcon(
        String(value),
        tableColumnHeader.enumSet?.options,
      )
      return (
        <div className="flex min-w-0 items-center gap-2">
          <EnumOptionMark color={color} iconName={iconName} />
          <span className="truncate">{String(value)}</span>
        </div>
      )
    }
    case DataType.BOOLEAN: {
      return (
        <Box sx={styles.checkbox}>
          <Checkbox checked={value === true} />
        </Box>
      )
    }
    case DataType.FORMULA_NUMBER:
    case DataType.NUMBER: {
      // A null FORMULA_NUMBER value here means the formula has no computed value (not
      // yet computed, or computed to null) -- the cell has already been denormalized (see the
      // isUndefined check above), so this isn't a loading state.
      const formattedValue = formatNumberColumnValue(value, tableColumnHeader, locale)
      return (
        <Box sx={{ ...styles.number, alignItems: 'center', gap: '4px' }}>
          <Text typography="body1">{formattedValue}</Text>
          {formulaErrorIndicator}
        </Box>
      )
    }
    case DataType.FORMULA_DATE:
    case DataType.DATE:
    case DataType.FORMULA_DATE_TIME:
    case DataType.DATE_TIME: {
      const formattedDate = formatDateColumnValue(
        value,
        {
          formatPreset: tableColumnHeader.formatPreset,
          formatOptions: tableColumnHeader.formatOptions,
          isDateTime:
            tableColumnHeader.type === DataType.DATE_TIME ||
            tableColumnHeader.type === DataType.FORMULA_DATE_TIME,
        },
        locale,
      )
      return (
        <Box sx={{ ...textWrap, ...styles.number, alignItems: 'center', gap: '4px' }}>
          <Text typography="body1">{formattedDate}</Text>
          {formulaErrorIndicator}
        </Box>
      )
    }
    // Boolean-output formula columns are out of scope (no create path)
    case DataType.FORMULA_BOOLEAN:
      return null
    case DataType.LOOKUP: {
      const lookupCell = first(cell.lookupCells)
      const lookupColumnId = tableColumnHeader.lookupColumn?.id
      if (lookupColumnId === undefined) {
        throw new Error('Lookup column is undefined')
      }

      const sourceEnum = enumSourceForLookupColumn(tableColumnHeader.lookupColumn)

      // Helper function to format lookup data based on the lookup column type
      const formatLookupData = (dataValue: string | number | boolean | unknown): string => {
        const lookupColumnType = tableColumnHeader.lookupColumn?.type
        const stringValue = String(dataValue)

        if (lookupColumnType === DataType.DATE) {
          return formatDate(stringValue, { localeCode: locale })
        } else if (lookupColumnType === DataType.DATE_TIME) {
          return formatDateTimeByLocale(stringValue, locale)
        }
        return stringValue
      }

      if (lookupCell) {
        const isUnauth = !user
        // For date/datetime lookup columns, use the raw ISO string from cell.data.data
        // instead of cell.userVisibleValue (which is GMT-formatted by Elasticsearch)
        const lookupColumnType = tableColumnHeader.lookupColumn?.type
        const shouldUseRawData =
          (lookupColumnType === DataType.DATE || lookupColumnType === DataType.DATE_TIME) &&
          cell.data &&
          isLookupCellData(cell.data) &&
          cell.data.data
        const displayValue =
          shouldUseRawData && cell.data && isLookupCellData(cell.data)
            ? String(cell.data.data)
            : cell.userVisibleValue
        const display = getCellDisplay(
          {
            ...lookupCell,
            userVisibleValue: displayValue,
          },
          lookupColumnId,
        )
        const lookup = {
          value: display ?? '',
          id: lookupCell.id,
          rowId: lookupCell.row.id,
          documents: lookupCell.documents,
        }
        const lookupIsNumericFormula = lookupColumnType === DataType.FORMULA_NUMBER
        const lookupFormulaErrorMessageKey = getFormulaErrorMessageKey(
          lookupCell.formulaError,
          lookupIsNumericFormula,
          numericFormulaRuntimeErrorsEnabled,
        )
        const lookupFormulaErrorMessage = lookupFormulaErrorMessageKey
          ? t(lookupFormulaErrorMessageKey)
          : undefined
        const lookupFormulaErrorIndicator = lookupFormulaErrorMessage ? (
          <Tooltip arrow title={lookupFormulaErrorMessage}>
            <span className="inline-flex shrink-0">
              <MdError className="text-red-8" size={16} />
            </span>
          </Tooltip>
        ) : null
        return (
          <Box sx={{ alignItems: 'center', display: 'flex', gap: '4px' }}>
            <LookupCellChipV2
              allowNavigate={!isUnauth}
              enumSource={{
                cell: { ...lookupCell, userVisibleValue: displayValue, data: cell.data },
                column: sourceEnum,
              }}
              lookup={lookup}
            />
            {lookupFormulaErrorIndicator}
          </Box>
        )
      } else if (isLookupCellData(cell.data) && cell.data.data) {
        // Display as chip if the cell has data but no lookup cells. This means the
        // user does not have permission to view the lookup cell but should still be able
        // to view the data in this cell
        const formattedData = formatLookupData(cell.data.data)
        const lookup = {
          value: formattedData,
        }
        return (
          <LookupCellChipV2
            allowNavigate={false}
            enumSource={{ column: sourceEnum }}
            lookup={lookup}
          />
        )
      } else {
        return <Box />
      }
    }
    case DataType.FILE_URL: {
      return fileContent
    }
    case DataType.USER: {
      const user = first(cell.users)
      const activeUser = activeUsers.find((activeUser) => activeUser.id === user?.id)

      if (user) {
        return (
          <div className="justify-center">
            <UserAvatar includeTooltip small user={activeUser ?? user} />
          </div>
        )
      } else {
        return <div />
      }
    }
    case DataType.ID: {
      if (typeof value === 'boolean' || Array.isArray(value)) {
        // Boolean values are not supported
        return null
      } else if (tableColumnHeader.indexAutogenerate && showAutogeneratedIdTooltip) {
        return (
          <Tooltip
            arrow
            title={t('table_cells.autogenerated_id_tooltip', {
              columnName: tableColumnHeader.name,
            })}
          >
            <Box
              sx={{
                ...textWrap,
                ...styles.autogeneratedIdCell,
                ...idTextFeatures,
              }}
            >
              <Text typography="body1">{value}</Text>
            </Box>
          </Tooltip>
        )
      }
      return (
        <Box sx={{ ...textWrap, ...idTextFeatures }}>
          <Text typography="body1">{value}</Text>
        </Box>
      )
    }
    case DataType.FORMULA_STRING:
    case DataType.STRING: {
      return (
        // Only show tooltip title if the text is being wrapped
        <Tooltip
          arrow
          leaveDelay={0}
          placement="bottom"
          title={maxColWidth >= TEXT_LENGTH_LIMIT ? value : ''}
        >
          <Box
            sx={{
              ...textWrap,
              whiteSpace: 'pre-line',
              ...(formulaErrorIndicator
                ? { display: 'flex', alignItems: 'flex-start', gap: '4px' }
                : {}),
            }}
          >
            <Linkify
              options={{
                render: {
                  url: ({ attributes, content }) => {
                    const { href, ...props } = attributes
                    return (
                      <Text href={href} {...props} typography="body1" variant="link">
                        {content}
                      </Text>
                    )
                  },
                  email: ({ attributes, content }) => {
                    const { ...props } = attributes
                    return (
                      <Text {...props} typography="body1" variant="link">
                        {content}
                      </Text>
                    )
                  },
                },
              }}
            >
              <Text typography="body1">{value}</Text>
            </Linkify>
            {formulaErrorIndicator}
          </Box>
        </Tooltip>
      )
    }
  }
}
